import { useMutation } from "@tanstack/react-query";
import { defineMessages, useIntl } from "react-intl";

import { QueryKeys, queryClient } from "@/api/core";
import { useApi } from "@/hooks";
import toast from "@/toast";

import type { Account, Relationship } from "@/schemas";

interface Variables {
  account: Account;
}

const messages = defineMessages({
  unmuteSuccess: {
    id: "accounts.unmute.success",
    defaultMessage: "You have successfully unmuted @{username}.",
  },
});

function useUnmuteAccount() {
  const api = useApi();
  const intl = useIntl();

  return useMutation({
    mutationFn: (variables: Variables) =>
      api.post<Relationship>(`/api/v1/accounts/${variables.account.id}/unmute`),
    async onMutate(variables) {
      const relationshipQueryKey = QueryKeys.relationships.byAccount(
        variables.account.id,
      );
      const accountQueryKey = QueryKeys.accounts.account(variables.account.id);

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: relationshipQueryKey });
      await queryClient.cancelQueries({ queryKey: accountQueryKey });

      // Snapshot the previous value
      const prevRelationship = queryClient.getQueryData(
        relationshipQueryKey,
      ) as Relationship;
      const prevAccount = queryClient.getQueryData(accountQueryKey) as Account;

      // Optimistically update to the new values
      const nextRelationship = { ...prevRelationship, muting: false };
      const nextAccount = { ...prevAccount, relationship: nextRelationship };
      queryClient.setQueryData(relationshipQueryKey, nextRelationship);
      queryClient.setQueryData(accountQueryKey, nextAccount);

      return { prevAccount, prevRelationship };
    },
    onSuccess(response, variables) {
      const queryKey = QueryKeys.relationships.byAccount(variables.account.id);
      queryClient.setQueryData(queryKey, response.data);

      // Render a toast
      toast.success(
        intl.formatMessage(messages.unmuteSuccess, {
          username: variables.account.acct,
        }),
      );
    },
    onError(error, variables, context) {
      // Return Relationship back to its original state
      if (context) {
        queryClient.setQueryData(
          QueryKeys.relationships.byAccount(variables.account.id),
          context.prevRelationship,
        );
        queryClient.setQueryData(
          QueryKeys.accounts.account(variables.account.id),
          context.prevAccount,
        );
      }
    },
  });
}

export { useUnmuteAccount };
